import { debounce } from "lodash";
import { RefObject, useLayoutEffect, useState } from "react";

// Matches the shared `max-h-72` rule on `.listbox-options` in ListBox.scss —
// keep both in sync, this is the threshold the upward flip triggers at.
export const LISTBOX_OPTIONS_MAX_HEIGHT = 288;

/**
 * Headless UI 1.x has no anchored-positioning support, so dropdown panels
 * always open downward by default and get clipped when the trigger sits
 * near the bottom of the viewport. Measure the available space the moment
 * the panel opens — regardless of what triggered it (button click, input
 * click while already focused, or keyboard) — and flip it upward when
 * there isn't enough room below.
 */
export const useOpenUpward = (
  wrapperRef: RefObject<HTMLElement>,
  open: boolean,
  maxPanelHeight: number,
): boolean => {
  const [openUpward, setOpenUpward] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      // Don't keep the last direction once closed — consumers apply the
      // upward styling based on this value alone, not gated by `open`.
      setOpenUpward(false);

      return;
    }

    const updateOpenDirection = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < maxPanelHeight && spaceAbove > spaceBelow);
    };

    updateOpenDirection();

    // On mobile, focusing the input (which opens the panel) also brings up
    // the on-screen keyboard, which shrinks window.innerHeight *after* this
    // measurement already ran. Re-measure whenever the viewport actually
    // changes size while the panel stays open. Debounced (matching
    // useIsMobile's pattern) since resize fires on every tick of the
    // on-screen-keyboard show/hide animation.
    const debouncedUpdate = debounce(updateOpenDirection, 100);
    window.addEventListener("resize", debouncedUpdate);
    window.visualViewport?.addEventListener("resize", debouncedUpdate);

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      window.visualViewport?.removeEventListener("resize", debouncedUpdate);
      debouncedUpdate.cancel();
    };
  }, [open, wrapperRef, maxPanelHeight]);

  return openUpward;
};
